import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { Button } from '@/components/ui/button.tsx';
import { format } from 'date-fns';

interface PESearchFormProps {
  searchBy: string;
  searchTerm: string;
  onSearchByChange: (value: string) => void;
  onSearchTermChange: (value: string) => void;
  onSearch: () => void;
  onReload: () => void;
  isLoading: boolean;
}

const PESearchForm: React.FC<PESearchFormProps> = ({
  searchBy,
  searchTerm,
  onSearchByChange,
  onSearchTermChange,
  onSearch,
  onReload,
  isLoading
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<string>('');
  const prevSearchByRef = useRef<string>(searchBy);
  const isInitialMount = useRef(true);

  // Generate year options (last 100 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 100 }, (_, i) => currentYear - i);

  // Month options
  const monthOptions = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Generate day options (1-31)
  const dayOptions = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  // Handle year selection
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    updateDateSearchTerm(year, selectedMonth, selectedDay);
  };

  // Handle month selection
  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateDateSearchTerm(selectedYear, month, selectedDay);
  };

  // Handle day selection
  const handleDayChange = (day: string) => {
    setSelectedDay(day);
    updateDateSearchTerm(selectedYear, selectedMonth, day);
  };

  // Update search term when year/month/day changes - filter immediately on any selection
  const updateDateSearchTerm = (year: string, month: string, day: string) => {
    // Build partial date string based on selected parts
    const parts = [];
    if (month) parts.push(month);
    if (day) parts.push(day);
    if (year) parts.push(year);

    // Join with "/" for partial matching
    const searchValue = parts.join('/');
    onSearchTermChange(searchValue);
  };

  // Handle date selection (legacy support)
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      onSearchTermChange(format(date, 'MM/dd/yyyy'));
    } else {
      onSearchTermChange('');
    }
  };

  // Clear inputs when search type changes (skip initial mount to preserve restored filters)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (prevSearchByRef.current !== searchBy) {
      setSelectedDate(undefined);
      setSelectedYear('');
      setSelectedMonth('');
      setSelectedDay('');
      onSearchTermChange('');
      prevSearchByRef.current = searchBy;
    }
  }, [searchBy, onSearchTermChange]);

  const handleReload = () => {
    setSelectedDate(undefined);
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    onSearchTermChange('');
    onReload();
  };

  const renderSearchInput = () => {
    switch (searchBy) {
      case 'dob':
         return (
          <div className="flex items-center space-x-2 w-full">
            <Select value={selectedMonth} onValueChange={handleMonthChange}>
              <SelectTrigger className="flex-1 min-w-32 bg-white border-gray-300 z-10" data-testid="pe-search-month-select">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="bg-white border shadow-lg z-20">
                {monthOptions.map((month) => (
                  <SelectItem key={month.value} value={month.value} data-testid={`pe-search-month-${month.value}`}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedDay} onValueChange={handleDayChange}>
              <SelectTrigger className="flex-1 min-w-20 bg-white border-gray-300 z-10" data-testid="pe-search-day-select">
                <SelectValue placeholder="Day" />
              </SelectTrigger>
              <SelectContent className="bg-white border shadow-lg z-20 max-h-60">
                {dayOptions.map((day) => (
                  <SelectItem key={day} value={day} data-testid={`pe-search-day-${day}`}>
                    {day}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedYear} onValueChange={handleYearChange}>
              <SelectTrigger className="flex-1 min-w-24 bg-white border-gray-300 z-10" data-testid="pe-search-year-select">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="bg-white border shadow-lg z-20 max-h-60">
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()} data-testid={`pe-search-year-${year}`}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

       default:
        return (
          <Input
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            className="w-full"
            data-testid="pe-search-input"
          />
        );
    }
  };

   return (
    <div className="flex items-center space-x-4 flex-1" data-testid="pe-search-form">
      <Select value={searchBy} onValueChange={onSearchByChange}>
        <SelectTrigger className="w-48 flex-shrink-0" data-testid="pe-search-by-select">
          <SelectValue placeholder="Search by..." />
        </SelectTrigger>
        <SelectContent className="bg-white border shadow-lg z-50">
          <SelectItem value="name" data-testid="pe-search-by-name">Patient Name</SelectItem>
          <SelectItem value="dob" data-testid="pe-search-by-dob">DOB</SelectItem>
          <SelectItem value="emr_id" data-testid="pe-search-by-emr-id">EMR ID</SelectItem>
          <SelectItem value="clinic" data-testid="pe-search-by-clinic">Clinic Name</SelectItem>
        </SelectContent>
      </Select>

      <div className="flex-1">
        {renderSearchInput()}
      </div>

      <div className="flex items-center space-x-2 flex-shrink-0">
        <Button
          onClick={onSearch}
          disabled={isLoading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6"
          data-testid="pe-search-button"
        >
          {isLoading ? 'Loading...' : 'Search'}
        </Button>
      </div>
    </div>
  );
};

export default PESearchForm;
